import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/client';
import SwipeCard from '../components/SwipeCard';
import RevealModal from '../components/RevealModal';

export default function Deck() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mediaType = params.get('type') === 'show' ? 'show' : 'movie';

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reveal, setReveal] = useState(null);
  const fetching = useRef(false);

  const loadMore = useCallback(async () => {
    if (fetching.current) return;
    fetching.current = true;
    try {
      const res = await api.get('/api/deck', {
        params: { media_type: mediaType, limit: 15 },
      });
      setCards((prev) => {
        const existing = new Set(prev.map((c) => c.id));
        const incoming = res.data.filter((c) => !existing.has(c.id));
        return [...prev, ...incoming];
      });
      setError('');
    } catch (e) {
      setError('Could not load cards. Is the library synced?');
    } finally {
      setLoading(false);
      fetching.current = false;
    }
  }, [mediaType]);

  useEffect(() => {
    setCards([]);
    setLoading(true);
    loadMore();
  }, [loadMore]);

  useEffect(() => {
    if (!loading && cards.length > 0 && cards.length <= 3) {
      loadMore();
    }
  }, [cards.length, loading, loadMore]);

  const handleDecision = async (decision) => {
    const top = cards[0];
    if (!top) return;
    setCards((prev) => prev.slice(1));
    try {
      const res = await api.post('/api/swipe', {
        title_id: top.id,
        decision,
      });
      if (decision === 'like' && res.data.reveal) {
        setReveal(res.data.reveal);
      }
    } catch (e) {
      // Re-queue the card if the swipe failed to record.
      setCards((prev) => [top, ...prev]);
    }
  };

  const topTwo = cards.slice(0, 2);

  return (
    <div className="mp-deck">
      <header className="mp-deck-header">
        <button className="mp-back" onClick={() => navigate('/')}>
          ‹
        </button>
        <span className="mp-deck-title">
          {mediaType === 'show' ? 'TV Shows' : 'Movies'}
        </span>
        <span className="mp-deck-spacer" />
      </header>

      <div className="mp-deck-stage">
        {loading && <div className="mp-deck-msg">Loading…</div>}

        {!loading && cards.length === 0 && (
          <div className="mp-deck-empty">
            <p>You've seen everything here for now.</p>
            <button className="mp-pill-btn" onClick={() => navigate('/watchlist')}>
              View watchlist
            </button>
            {error && <p className="mp-deck-error">{error}</p>}
          </div>
        )}

        {topTwo
          .map((card, i) => (
            <SwipeCard
              key={card.id}
              card={card}
              isTop={i === 0}
              onDecision={handleDecision}
            />
          ))
          .reverse()}
      </div>

      {cards.length > 0 && (
        <div className="mp-action-bar">
          <button
            className="mp-action mp-action--pass"
            onClick={() => handleDecision('pass')}
            aria-label="Skip"
          >
            ✕
          </button>
          <button
            className="mp-action mp-action--seen"
            onClick={() => handleDecision('seen')}
            aria-label="Already seen"
          >
            👁
          </button>
          <button
            className="mp-action mp-action--like"
            onClick={() => handleDecision('like')}
            aria-label="Add to watchlist"
          >
            ♥
          </button>
        </div>
      )}

      <RevealModal reveal={reveal} onClose={() => setReveal(null)} />
    </div>
  );
}
